import { useMediaQuery } from '@mui/material';
import { SxProps } from '@mui/material/styles';
import {
  DataGridPro as DataGrid,
  DataGridProProps as DataGridProps,
  GridLocaleText,
} from '@mui/x-data-grid-pro';
import { useMemo } from 'react';
import { useLanguage } from '../../contexts/language';

const CustomDataGrid = ({ sx, ...props }: DataGridProps) => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const customSx: SxProps = {
    // overrides the general fullWidth setting for select components, which is set via the MUI theme
    '& .MuiTablePagination-select': {
      width: 'auto',
    },
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    ...(Array.isArray(sx) ? Object.assign({}, ...sx) : sx),
  };

  const { t } = useLanguage();

  const localeText = useMemo<Partial<GridLocaleText>>(() => {
    return {
      columnMenuLabel: t('data-grid.menu'),
      columnHeaderSortIconLabel: t('data-grid.sort'),
      columnMenuFilter: t('data-grid.filter'),
      columnMenuSortAsc: t('data-grid.sort-asc'),
      columnMenuSortDesc: t('data-grid.sort-desc'),
      columnMenuHideColumn: t('data-grid.hide-column'),
      columnMenuShowColumns: t('data-grid.show-columns'),
      columnMenuUnsort: t('data-grid.unsort'),
      columnMenuManageColumns: t('data-grid.manage-columns'),
      columnsManagementSearchTitle: t('data-grid.search'),
      columnsManagementShowHideAllText: t('data-grid.show-hide-all'),
      columnsManagementReset: t('data-grid.reset'),
      filterPanelColumns: t('data-grid.columns'),
      filterPanelInputPlaceholder: t('data-grid.filter-value'),
      filterPanelOperator: t('data-grid.operator.label'),
      filterPanelInputLabel: t('data-grid.filter-value'),
      pinToLeft: t('data-grid.pin-to-left'),
      pinToRight: t('data-grid.pin-to-right'),
      unpin: t('data-grid.unpin'),
      filterPanelAddFilter: t('data-grid.add-filter'),
      filterPanelRemoveAll: t('data-grid.remove-all'),
      noResultsOverlayLabel: t("content.no-results-found"),
      // Operators
      filterPanelOperatorAnd: t('data-grid.operator.and'),
      filterPanelOperatorOr: t('data-grid.operator.or'),
      filterOperatorIs: t('data-grid.operator.is'),
      filterOperatorNot: t('data-grid.operator.is-not'),
      filterOperatorIsAnyOf: t('data-grid.operator.is-any-of'),
      filterOperatorIsEmpty: t('data-grid.operator.is-empty'),
      filterOperatorIsNotEmpty: t('data-grid.operator.is-not-empty'),
      filterOperatorAfter: t('data-grid.operator.date-is-after'),
      filterOperatorOnOrAfter: t('data-grid.operator.date-is-on-or-after'),
      filterOperatorBefore: t('data-grid.operator.date-is-before'),
      filterOperatorOnOrBefore: t('data-grid.operator.date-is-on-or-before'),
      filterOperatorContains: t('data-grid.operator.contains'),
      filterOperatorDoesNotContain: t('data-grid.operator.does-not-contain'),
      filterOperatorEquals: t('data-grid.operator.equals'),
      filterOperatorDoesNotEqual: t('data-grid.operator.does-not-equal'),
      filterOperatorStartsWith: t('data-grid.operator.starts-with'),
      filterOperatorEndsWith: t('data-grid.operator.ends-with'),
    };
  }, [t]);

  const isMdDown = useMediaQuery('(max-width:1023px)');

  return (
    <div className="overflow-x-auto">
      <div className="flex flex-col">
        <DataGrid
          localeText={localeText}
          sx={customSx}
          multipleColumnsSortingMode="always"
          slotProps={{
            baseInput: { fullWidth: false },
            baseTextField: { fullWidth: false },
            baseSelect: { fullWidth: false },
            ...props.slotProps,
          }}
          density={isMdDown ? 'compact' : 'standard'}
          {...props}
        />
      </div>
    </div>
  );
};

export default CustomDataGrid;
